export interface AuroraSeedBlob {
  initialPlacement?: {
    top?: string
    bottom?: string
    left?: string
    right?: string
    width?: string
    height?: string
  }
  bounds: {
    minX: number
    maxX: number
    minY: number
    maxY: number
  }
}

export interface AuroraSeedConfig {
  name: string
  blobs: [AuroraSeedBlob, AuroraSeedBlob, AuroraSeedBlob, AuroraSeedBlob]
}

export interface AuroraBackgroundProps {
  className?: string
  seed?: number | string
}

export const AURORA_SEEDS: AuroraSeedConfig[] = [
  // Seed 0: Broad Horizon Sweep (East-West flow across wide coordinates)
  {
    name: 'Horizon Sweep',
    blobs: [
      {
        initialPlacement: { top: '-8%', left: '2%', width: '560px', height: '560px' },
        bounds: { minX: -160, maxX: 340, minY: -80, maxY: 260 },
      },
      {
        initialPlacement: { top: '4%', right: '2%', width: '580px', height: '580px' },
        bounds: { minX: -340, maxX: 180, minY: -70, maxY: 280 },
      },
      {
        initialPlacement: { top: '34%', left: '14%', width: '500px', height: '500px' },
        bounds: { minX: -280, maxX: 280, minY: -150, maxY: 250 },
      },
      {
        initialPlacement: { top: '26%', right: '14%', width: '520px', height: '520px' },
        bounds: { minX: -300, maxX: 260, minY: -130, maxY: 290 },
      },
    ],
  },
  // Seed 1: Diagonal Cascade (Northwest to Southeast dynamic drift)
  {
    name: 'Diagonal Cascade',
    blobs: [
      {
        initialPlacement: { top: '-14%', left: '10%', width: '580px', height: '580px' },
        bounds: { minX: -220, maxX: 360, minY: -60, maxY: 320 },
      },
      {
        initialPlacement: { top: '18%', right: '4%', width: '520px', height: '520px' },
        bounds: { minX: -360, maxX: 160, minY: -120, maxY: 280 },
      },
      {
        initialPlacement: { top: '44%', left: '6%', width: '480px', height: '480px' },
        bounds: { minX: -180, maxX: 340, minY: -170, maxY: 240 },
      },
      {
        initialPlacement: { top: '28%', right: '20%', width: '540px', height: '540px' },
        bounds: { minX: -280, maxX: 240, minY: -140, maxY: 300 },
      },
    ],
  },
  // Seed 2: Ambient Orbit (Vast orbital dispersion across canvas quadrants)
  {
    name: 'Ambient Orbit',
    blobs: [
      {
        initialPlacement: { top: '2%', left: '-5%', width: '600px', height: '600px' },
        bounds: { minX: -100, maxX: 380, minY: -100, maxY: 280 },
      },
      {
        initialPlacement: { top: '-12%', right: '10%', width: '540px', height: '540px' },
        bounds: { minX: -380, maxX: 120, minY: -80, maxY: 300 },
      },
      {
        initialPlacement: { top: '38%', right: '2%', width: '500px', height: '500px' },
        bounds: { minX: -320, maxX: 220, minY: -170, maxY: 240 },
      },
      {
        initialPlacement: { top: '24%', left: '20%', width: '540px', height: '540px' },
        bounds: { minX: -240, maxX: 320, minY: -130, maxY: 300 },
      },
    ],
  },
  // Seed 3: Dual-Core Surge (Dynamic center-split and reconvergence)
  {
    name: 'Dual-Core Surge',
    blobs: [
      {
        initialPlacement: { top: '-4%', left: '20%', width: '560px', height: '560px' },
        bounds: { minX: -320, maxX: 320, minY: -80, maxY: 300 },
      },
      {
        initialPlacement: { top: '6%', right: '18%', width: '540px', height: '540px' },
        bounds: { minX: -340, maxX: 300, minY: -80, maxY: 300 },
      },
      {
        initialPlacement: { top: '30%', left: '4%', width: '500px', height: '500px' },
        bounds: { minX: -180, maxX: 380, minY: -150, maxY: 260 },
      },
      {
        initialPlacement: { top: '40%', right: '6%', width: '520px', height: '520px' },
        bounds: { minX: -380, maxX: 200, minY: -170, maxY: 260 },
      },
    ],
  },
  // Seed 4: Vertical Atmospheric Flow (Upper to lower fluid currents)
  {
    name: 'Vertical Flow',
    blobs: [
      {
        initialPlacement: { top: '-16%', left: '5%', width: '520px', height: '620px' },
        bounds: { minX: -200, maxX: 300, minY: -60, maxY: 360 },
      },
      {
        initialPlacement: { top: '-8%', right: '6%', width: '540px', height: '600px' },
        bounds: { minX: -300, maxX: 220, minY: -80, maxY: 340 },
      },
      {
        initialPlacement: { top: '42%', left: '22%', width: '520px', height: '520px' },
        bounds: { minX: -280, maxX: 280, minY: -200, maxY: 260 },
      },
      {
        initialPlacement: { top: '16%', right: '26%', width: '500px', height: '540px' },
        bounds: { minX: -260, maxX: 300, minY: -130, maxY: 320 },
      },
    ],
  },
  // Seed 5: Cross-Current Swirl (Opposing directional vortex with energetic shifts)
  {
    name: 'Cross-Current Swirl',
    blobs: [
      {
        initialPlacement: { top: '-10%', right: '24%', width: '550px', height: '550px' },
        bounds: { minX: -340, maxX: 260, minY: -90, maxY: 310 },
      },
      {
        initialPlacement: { top: '12%', left: '-2%', width: '570px', height: '570px' },
        bounds: { minX: -140, maxX: 380, minY: -110, maxY: 290 },
      },
      {
        initialPlacement: { top: '36%', left: '26%', width: '510px', height: '510px' },
        bounds: { minX: -290, maxX: 290, minY: -160, maxY: 270 },
      },
      {
        initialPlacement: { top: '22%', right: '4%', width: '530px', height: '530px' },
        bounds: { minX: -360, maxX: 190, minY: -130, maxY: 310 },
      },
    ],
  },
]

export const getSeedIndex = (seed: number | string | undefined): number => {
  if (typeof seed === 'number') {
    return Math.abs(Math.floor(seed)) % AURORA_SEEDS.length
  }
  if (typeof seed === 'string') {
    let hash = 0
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i)
      hash |= 0
    }
    return Math.abs(hash) % AURORA_SEEDS.length
  }
  return Math.floor(Math.random() * AURORA_SEEDS.length)
}
